import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { FaWhatsapp } from 'react-icons/fa';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import WhatsAppFloat from '../../components/WhatsAppFloat';
import Icon from '../../components/AppIcon';
import ContactHero from './components/ContactHero';
import ContactMethods from './components/ContactMethods';
import QuickContactOptions from './components/QuickContactOptions';
import ExpertTeam from './components/ExpertTeam';
import ContactFAQ, { CONTACT_FAQS } from './components/ContactFAQ';
import { SITE, WA_MESSAGES, WHATSAPP_DISPLAY, openWhatsApp } from '../../config/site';

const VIPContactSuiteWhatsAppHub = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleWhatsAppClick = () => openWhatsApp(WA_MESSAGES.general);

  return (
    <>
      <Helmet>
        {/* Deindexed: keep site reachable directly, but out of search results. */}
        <meta name="robots" content="noindex, nofollow, noarchive, nosnippet, noimageindex" />
        <meta name="googlebot" content="noindex, nofollow, noarchive, nosnippet, noimageindex" />
        <title>VIP Contact Suite - WhatsApp Hub | Nitin Online Book | Nitin Online Book</title>
        <meta
          name="description"
          content="Connect with India's premier gaming experts at Nitin Online Book and Nitin Online Book through WhatsApp. Instant support for Nitin online book login, sign up, app download and payouts. 24/7 VIP customer service."
        />
        <meta
          name="keywords"
          content="Nitin Online Book WhatsApp, Nitin Online Book support, WhatsApp support, gaming experts, VIP customer service, instant chat, gaming consultation, Nitin support, Nitin online book help, Nitin online book contact"
        />
        <meta property="og:title" content="VIP Contact Suite - WhatsApp Hub | Nitin Online Book | Nitin Online Book" />
        <meta
          property="og:description"
          content="Premium gaming support through WhatsApp. Certified experts for Nitin online book login, sign up, app download and personalised recommendations — 24/7."
        />
        <meta property="og:image" content={`${SITE.url}/assets/images/gen/contact-banner.svg`} />
        <meta property="og:url" content={`${SITE.url}/vip-contact-suite-whats-app-hub`} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content={SITE.name} />
        <meta property="og:locale" content="en_IN" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="VIP Contact Suite - WhatsApp Hub | Nitin Online Book | Nitin Online Book" />
        <meta
          name="twitter:description"
          content="Talk to certified Nitin Online Book experts on WhatsApp for instant gaming support, 24/7."
        />
        <meta name="twitter:image" content={`${SITE.url}/assets/images/gen/contact-banner.svg`} />
        <link rel="canonical" href={`${SITE.url}/vip-contact-suite-whats-app-hub`} />

        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE.url}/` },
              {
                '@type': 'ListItem',
                position: 2,
                name: 'Contact',
                item: `${SITE.url}/vip-contact-suite-whats-app-hub`,
              },
            ],
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: CONTACT_FAQS.map((f) => ({
              '@type': 'Question',
              name: f.q,
              acceptedAnswer: { '@type': 'Answer', text: f.a },
            })),
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />
        <ContactHero onWhatsAppClick={handleWhatsAppClick} />
        <ContactMethods />
        <QuickContactOptions />
        <ExpertTeam />
        <ContactFAQ />

        {/* Closing CTA */}
        <section className="relative overflow-hidden py-20">
          <span className="aurora left-1/2 top-0 h-80 w-[36rem] -translate-x-1/2 bg-wa-green/20" aria-hidden="true" />
          <div className="relative mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
            <div className="panel-gold shine-hover overflow-hidden px-6 py-12 text-center sm:px-12">
              <span className="mx-auto mb-6 flex h-20 w-20 animate-float items-center justify-center rounded-full bg-gradient-royal shadow-glow">
                <Icon name="Crown" size={36} color="#150F02" />
              </span>

              <h2 className="font-heading text-3xl font-bold text-white sm:text-4xl">
                Ready for the <span className="gold-text">royal treatment</span>?
              </h2>
              <p className="mx-auto mt-4 max-w-2xl text-white/60">
                Thousands of players run their entire gaming life through this one number — sign up, strategy,
                cashier, everything. Send a message and see how different support can feel.
              </p>

              <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
                <button onClick={handleWhatsAppClick} className="btn-3d btn-3d-wa">
                  <FaWhatsapp size={20} /> Start your VIP experience
                </button>
                <span className="flex items-center gap-2 text-sm text-white/55">
                  <span className="relative flex h-2.5 w-2.5">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-wa-green opacity-75" />
                    <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-wa-green" />
                  </span>
                  3 gaming experts online now
                </span>
              </div>

              <div className="mt-10 grid gap-4 border-t border-white/10 pt-8 sm:grid-cols-3">
                {[
                  { icon: 'ShieldCheck', label: '100% secure & private', color: '#25D366' },
                  { icon: 'Clock', label: '24/7 expert support', color: '#F0C24B' },
                  { icon: 'Star', label: '4.9/5 customer rating', color: '#FF8ED0' },
                ].map((t) => (
                  <div key={t.label} className="flex items-center justify-center gap-2 text-sm text-white/60">
                    <Icon name={t.icon} size={17} color={t.color} />
                    {t.label}
                  </div>
                ))}
              </div>

              <p className="mt-6 text-xs text-white/35">Direct line · {WHATSAPP_DISPLAY} · {SITE.domain}</p>
            </div>
          </div>
        </section>

        <Footer />
        <WhatsAppFloat />
      </div>
    </>
  );
};

export default VIPContactSuiteWhatsAppHub;
